export const DEFAULT_AUTHENTICATED_PATH = '/app';

const SCHEME_PATTERN = /(^|[/?#&=])[A-Za-z][A-Za-z0-9+.-]*:/u;
const PROTOCOL_RELATIVE_PATTERN = /(^|[?&#=])\/\//u;
const RESET_TOKEN_PATTERN = /(^|[?&#])token(?:=|[&#]|$)/iu;
const AUTHENTICATION_PATHS = new Set(['/login', '/reset-password']);

function decodedRepresentations(value: string): string[] | null {
  const values = [value];
  let current = value;
  for (let pass = 0; pass < 4; pass += 1) {
    let decoded: string;
    try {
      decoded = decodeURIComponent(current);
    } catch {
      return null;
    }
    if (decoded === current) return values;
    values.push(decoded);
    current = decoded;
  }
  // A still-encoded percent sign after four passes is unnecessary for a
  // Flow Wizard route and keeps an ambiguous extra decoding layer alive.
  return /%25/iu.test(current) ? null : values;
}

function representationIsUnsafe(value: string): boolean {
  const containsControlCharacter = Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x1f || codePoint === 0x7f;
  });
  return (
    !value.startsWith('/') ||
    value.startsWith('//') ||
    PROTOCOL_RELATIVE_PATTERN.test(value) ||
    value.includes('\\') ||
    containsControlCharacter ||
    SCHEME_PATTERN.test(value)
  );
}

function containsResetSecret(value: URL): boolean {
  if (AUTHENTICATION_PATHS.has(value.pathname.replace(/\/$/u, ''))) {
    return value.pathname.replace(/\/$/u, '') === '/reset-password';
  }
  const parameters = decodedRepresentations(`${value.search}${value.hash}`);
  return parameters === null || parameters.some((item) => RESET_TOKEN_PATTERN.test(item));
}

/**
 * Return one normalized same-origin router destination, or null when a value
 * could represent an external target or password-reset capability.
 */
export function normalizeInternalReturnTo(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0 || value !== value.trim()) {
    return null;
  }
  const representations = decodedRepresentations(value);
  if (
    representations === null ||
    representations.some(representationIsUnsafe)
  ) {
    return null;
  }

  const base = new URL('https://flow-wizard.invalid');
  let parsed: URL;
  try {
    parsed = new URL(value, base);
  } catch {
    return null;
  }
  if (
    parsed.origin !== base.origin ||
    parsed.username !== '' ||
    parsed.password !== '' ||
    representationIsUnsafe(`${parsed.pathname}${parsed.search}${parsed.hash}`) ||
    containsResetSecret(parsed)
  ) {
    return null;
  }
  const normalizedPath = parsed.pathname.replace(/\/$/u, '') || '/';
  if (AUTHENTICATION_PATHS.has(normalizedPath)) return null;
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

export function authenticatedReturnFromState(state: unknown): string {
  if (typeof state !== 'object' || state === null || !('returnTo' in state)) {
    return DEFAULT_AUTHENTICATED_PATH;
  }
  return normalizeInternalReturnTo(state.returnTo) ?? DEFAULT_AUTHENTICATED_PATH;
}

export function protectedLocationReturn(
  location: Pick<Location, 'hash' | 'pathname' | 'search'>,
): string {
  return (
    normalizeInternalReturnTo(
      `${location.pathname}${location.search}${location.hash}`,
    ) ?? DEFAULT_AUTHENTICATED_PATH
  );
}
